<template>
    <div v-if="visible"
         class="landlord-media-progress"
         :class="rootClasses"
         role="status"
         aria-live="polite">
        <div class="landlord-media-progress__head">
            <span class="landlord-media-progress__title">{{ title }}</span>
            <span class="landlord-media-progress__percent" v-if="percentLabel">{{ percentLabel }}</span>
        </div>
        <div class="landlord-media-progress__track" :class="{ 'is-indeterminate': indeterminate }">
            <div class="landlord-media-progress__fill"
                 :class="{ 'is-no-transition': suppressTransition }"
                 :style="indeterminate ? null : { width: fillWidth }"></div>
        </div>
        <div class="landlord-media-progress__meta">
            <span class="landlord-media-progress__counter">{{ counterLabel }}</span>
            <span v-if="bytesLabel" class="landlord-media-progress__bytes">{{ bytesLabel }}</span>
            <span v-if="failed > 0" class="landlord-media-progress__failed">{{ failedLabel }}</span>
        </div>
    </div>
</template>

<script>
function formatBytes(bytes) {
    if (bytes === null || bytes === undefined || isNaN(bytes) || bytes <= 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default {
    name: 'LandlordMediaBatchProgress',
    data: function () {
        return { suppressTransition: false };
    },
    watch: {
        mode: function (newVal, oldVal) {
            if (newVal === oldVal) return;
            // Mode flips (upload -> delete) must reset the bar instantly without animating
            // the previous fill width down/up to the new target.
            this.suppressTransition = true;
            var self = this;
            this.$nextTick(function () {
                setTimeout(function () { self.suppressTransition = false; }, 60);
            });
        },
    },
    props: {
        visible:     { type: Boolean, default: true },
        title:       { type: String,  default: '' },
        done:        { type: Number,  default: 0 },
        total:       { type: Number,  default: 0 },
        failed:      { type: Number,  default: 0 },
        pending:     { type: Number,  default: 0 },
        bytesLoaded: { type: Number,  default: 0 },
        bytesTotal:  { type: Number,  default: 0 },
        mode:        { type: String,  default: 'upload' },
        status:      { type: String,  default: 'active' },
        indeterminate: { type: Boolean, default: false },
        compact:     { type: Boolean, default: false },
        fading:      { type: Boolean, default: false },
        lang:        { type: String,  default: 'nl' },
    },
    computed: {
        rootClasses() {
            var finalFailure = this.failed > 0 && this.pending <= 0;
            return {
                'landlord-media-progress--compact':       this.compact,
                'landlord-media-progress--final-failure': finalFailure,
                'landlord-media-progress--has-failed':    this.failed > 0 && !finalFailure,
                'landlord-media-progress--done':          this.status === 'done',
                'landlord-media-progress--delete':        this.mode === 'delete',
                'landlord-media-progress--processing':    this.mode === 'processing',
                'landlord-media-progress--is-fading':     this.fading,
            };
        },
        percent() {
            if (this.indeterminate || this.total <= 0) return 0;
            if (this.bytesTotal > 0 && this.bytesLoaded >= 0) {
                return Math.max(0, Math.min(100, Math.round((this.bytesLoaded / this.bytesTotal) * 100)));
            }
            return Math.max(0, Math.min(100, Math.round((this.done / this.total) * 100)));
        },
        percentLabel() {
            if (this.indeterminate) return '';
            if (this.total <= 0) return '';
            return this.percent + '%';
        },
        fillWidth() {
            return this.percent + '%';
        },
        counterLabel() {
            if (this.indeterminate) {
                return this.lang === 'nl' ? 'Bezig...' : 'Working...';
            }
            var doneWord = this.lang === 'nl' ? 'van' : 'of';
            var readyWord = this.modeReadyWord();
            return this.done + ' ' + doneWord + ' ' + this.total + ' ' + readyWord;
        },
        bytesLabel() {
            if (this.bytesTotal <= 0) return '';
            var loaded = formatBytes(this.bytesLoaded);
            var total  = formatBytes(this.bytesTotal);
            if (!total) return '';
            return loaded ? (loaded + ' / ' + total) : total;
        },
        failedLabel() {
            if (this.failed <= 0) return '';
            return this.lang === 'nl'
                ? this.failed + ' mislukt'
                : this.failed + ' failed';
        },
    },
    methods: {
        modeReadyWord() {
            if (this.mode === 'delete') {
                return this.lang === 'nl' ? 'verwijderd' : 'removed';
            }
            if (this.mode === 'processing') {
                return this.lang === 'nl' ? 'verwerkt' : 'processed';
            }
            return this.lang === 'nl' ? 'klaar' : 'done';
        },
    },
};
</script>
